import { type Cleaners, isValidValue, type RowPredicate } from './common';

type StrategyProduct = 'vanilla' | 'trigger' | 'binary';

/* eslint-disable no-shadow */
enum Col {
  Product = 0,
  Way = 1,
  Type = 2,
  ExpiryDate = 3,
  Strike = 4,
  Notional = 5,
  NotionalCcy = 6,
  DeliveryDate = 7,
}

export type StrategyParseError = 'INVALID_PRODUCT' | 'INVALID_WAY' | 'INVALID_TYPE' | 'INVALID_NOTIONAL_CCY';

export interface ImportedStrategyRow {
  product: StrategyProduct;
  way: 'buy' | 'sell';
  type: 'call' | 'put';
  expiryDate: string;
  strike: string;
  notional: string;
  notionalCcy: string;
  deliveryDate?: string;
}

const products = ['vanilla', 'trigger', 'binary'];
const validateProduct: RowPredicate = (row) => isValidValue(products, row[Col.Product]);

const ways = ['buy', 'sell'];
const validateWay: RowPredicate = (row) => isValidValue(ways, row[Col.Way]);

const types = ['call', 'put'];
const validateType: RowPredicate = (row) => isValidValue(types, row[Col.Type]);

const validateNotionalCcy =
  (strategyCurrencies: [string, string]): RowPredicate =>
  (row, _rows) =>
    isValidValue(strategyCurrencies, row[Col.NotionalCcy]);

export const generateValidators = (ccyPair: string): Record<StrategyParseError, RowPredicate> => ({
  INVALID_PRODUCT: validateProduct,
  INVALID_WAY: validateWay,
  INVALID_TYPE: validateType,
  INVALID_NOTIONAL_CCY: ccyPair
    ? validateNotionalCcy(ccyPair.split('/').map((s) => s.toLowerCase()) as [string, string])
    : () => false,
});

export const mapStrategyRow =
  ({ cleanupNumber, sanitizeDate }: Cleaners) =>
  (parsedRow: readonly unknown[]): ImportedStrategyRow => ({
    product: toCellString(parsedRow[Col.Product]).toLowerCase() as StrategyProduct,
    way: toCellString(parsedRow[Col.Way]).toLowerCase() as ImportedStrategyRow['way'],
    type: toCellString(parsedRow[Col.Type]).toLowerCase() as ImportedStrategyRow['type'],
    expiryDate: sanitizeDate(toCellString(parsedRow[Col.ExpiryDate])),
    strike: cleanupNumber(toCellString(parsedRow[Col.Strike])),
    notional: cleanupNumber(toCellString(parsedRow[Col.Notional]), true),
    notionalCcy: toCellString(parsedRow[Col.NotionalCcy]),
    deliveryDate: sanitizeDate(toCellString(parsedRow[Col.DeliveryDate])),
  });

export const requiredColumns = [
  'Product',
  'Way',
  'Type',
  'Expiry Date',
  'Strike',
  'Notional',
  'Notional CCY',
  'Delivery Date',
];

function toCellString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return '';
}
